import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { SpeakerHigh } from "@phosphor-icons/react";
import { LUMI_POSES, LUMI_ALT, LUMI_MESSAGES, pickMessage } from "@/data/lumiPoses";
import "./lumi.css";

// Grown-up pages show Lumi inside the page content instead of the floating sticker (so she never covers buttons).
const KIDS_FREE_ROUTES = new Set(["/", "/resources", "/privacy", "/childrens-privacy", "/terms", "/refund-policy", "/contact"]);

// ----------------------------------------------------------------------
// Lumi the Word Fairy — global companion (lightweight: CSS keyframes only)
// ----------------------------------------------------------------------

const LUMI_SPRITES = {
  idle: LUMI_POSES.sitting,
  speaking: LUMI_POSES.speaking,
  question: LUMI_POSES.speaking,
  celebrate: LUMI_POSES.celebrating,
  wave: LUMI_POSES.waving,
  reading: LUMI_POSES.reading,
  thinking: LUMI_POSES.thinking,
  tryagain: LUMI_POSES.tryAgain,
  flying: LUMI_POSES.flying,
};

const SPRITE_ALT = {
  idle: LUMI_ALT.sitting,
  speaking: LUMI_ALT.speaking,
  question: LUMI_ALT.speaking,
  celebrate: LUMI_ALT.celebrating,
  wave: LUMI_ALT.waving,
  reading: LUMI_ALT.reading,
  thinking: LUMI_ALT.thinking,
  tryagain: LUMI_ALT.tryAgain,
  flying: LUMI_ALT.flying,
};

const VALID_STATES = Object.keys(LUMI_SPRITES);

const POSITIVE_MESSAGES = LUMI_MESSAGES.celebrate;

const LumiContext = createContext(null);

export const useLumi = () => {
  const ctx = useContext(LumiContext);
  if (!ctx) {
    return {
      state: "idle", message: "", spokenSentence: null, activeWordIndex: -1,
      setState: () => {}, say: () => {}, ask: () => {}, celebrate: () => {},
      celebrateSentence: () => {}, wave: () => {}, idle: () => {},
      think: () => {}, tryAgain: () => {}, read: () => {}, encourage: () => {},
      inline: false, setInline: () => {},
      hide: () => {}, show: () => {},
      setActiveWordIndex: () => {},
    };
  }
  return ctx;
};

export const LumiProvider = ({ children }) => {
  const [state, setStateRaw] = useState("idle");
  const [message, setMessage] = useState("");
  const [visible, setVisible] = useState(true);
  const [inline, setInline] = useState(false); // mobile activities render Lumi inline instead of floating
  const [spokenSentence, setSpokenSentence] = useState(null);
  const [activeWordIndex, setActiveWordIndex] = useState(-1);

  const timersRef = useRef([]);

  const clearTimers = useCallback(() => {
    timersRef.current.forEach((t) => clearTimeout(t));
    timersRef.current = [];
  }, []);

  const cancelSpeech = useCallback(() => {
    if (typeof window !== "undefined" && window.speechSynthesis) {
      try { window.speechSynthesis.cancel(); } catch {}
    }
  }, []);

  useEffect(() => () => { clearTimers(); cancelSpeech(); }, [clearTimers, cancelSpeech]);

  const setState = useCallback((next) => {
    if (VALID_STATES.includes(next)) setStateRaw(next);
  }, []);

  // holdMood: the bubble still clears after durationMs, but the pose stays until the activity calls idle().
  const say = useCallback((text, mood = "speaking", durationMs = 5000, holdMood = false) => {
    clearTimers();
    setMessage(text || "");
    setStateRaw(VALID_STATES.includes(mood) ? mood : "speaking");
    if (durationMs > 0) {
      timersRef.current.push(setTimeout(() => setMessage(""), durationMs));
      if (!holdMood) timersRef.current.push(setTimeout(() => setStateRaw("idle"), durationMs + 400));
    }
  }, [clearTimers]);

  const ask = useCallback((text, durationMs = 6000) => say(text, "question", durationMs), [say]);
  const wave = useCallback((text = pickMessage("greeting"), durationMs = 3000) => say(text, "wave", durationMs), [say]);
  const celebrate = useCallback((text = "", durationMs = 3500) =>
    say(text || pickMessage("celebrate"), "celebrate", durationMs), [say]);
  // Supportive mistake feedback: first miss → thinking, later misses → try-again. Never "wrong"/"sad".
  const think = useCallback((text = "", durationMs = 3500) => say(text || pickMessage("thinking"), "thinking", durationMs), [say]);
  const tryAgain = useCallback((text = "", durationMs = 3500, holdMood = false) =>
    say(text || pickMessage("tryAgain"), "tryagain", durationMs, holdMood), [say]);
  const encourage = useCallback((attempt = 1, text = "", durationMs = 3500) =>
    (attempt <= 1 ? think(text, durationMs) : tryAgain(text, durationMs)), [think, tryAgain]);
  const read = useCallback((text = "", durationMs = 6000) => say(text, "reading", durationMs), [say]);

  const idle = useCallback(() => {
    clearTimers();
    cancelSpeech();
    setMessage("");
    setSpokenSentence(null);
    setActiveWordIndex(-1);
    setStateRaw("idle");
  }, [clearTimers, cancelSpeech]);

  // ----- Spec flow on correct sentence completion -----
  //   t=0   speaking + random positive bubble + read sentence aloud
  //   t=2s  celebrate
  //   t=4s  idle
  // holdPose (Sentence Builder): Celebrating from t=0 and held until the activity calls idle()
  // when the next round starts; only the bubble and word playback clear at t=4s.
  const celebrateSentence = useCallback((sentence, { holdPose = false } = {}) => {
    clearTimers();
    cancelSpeech();

    const cleaned = (sentence || "").trim();
    const tokens = cleaned ? cleaned.split(/\s+/) : [];
    const random = POSITIVE_MESSAGES[Math.floor(Math.random() * POSITIVE_MESSAGES.length)];

    setSpokenSentence({ words: tokens });
    setActiveWordIndex(-1);
    setMessage(random);
    setStateRaw(holdPose ? "celebrate" : "speaking");

    // Word highlighting is now driven by the server-side audio in SentenceBuilder
    // (SentenceBuilder plays /api/kids/lumi-speak audio then coaches). We no longer
    // use window.speechSynthesis here so the app uses ONE consistent voice everywhere.

    // t=2s -> celebrate (message stays visible per spec — full 4s lifetime)
    if (!holdPose) timersRef.current.push(setTimeout(() => setStateRaw("celebrate"), 2000));
    // t=4s -> idle and clear everything
    timersRef.current.push(setTimeout(() => {
      setMessage("");
      if (!holdPose) setStateRaw("idle");
      setActiveWordIndex(-1);
      setSpokenSentence(null);
    }, 4000));
  }, [clearTimers, cancelSpeech]);

  const hide = useCallback(() => setVisible(false), []);
  const show = useCallback(() => setVisible(true), []);

  const handleLumiTap = useCallback(() => {
    celebrate("You tapped me! ✨", 2000);
  }, [celebrate]);

  const value = {
    state, message, visible, spokenSentence, activeWordIndex,
    setState, say, ask, celebrate, celebrateSentence, wave, idle, hide, show,
    think, tryAgain, encourage, read,
    inline, setInline,
    setActiveWordIndex,
  };

  return (
    <LumiContext.Provider value={value}>
      {children}
      {visible && !KIDS_FREE_ROUTES.has(window.location.pathname) && <LumiCharacter state={state} message={message} onTap={handleLumiTap} inline={inline} />}
    </LumiContext.Provider>
  );
};

// ----------------------------------------------------------------------
// Per-word sentence playback (renders inside the result panel)
// ----------------------------------------------------------------------
export const LumiSentencePlayback = () => {
  const { spokenSentence, activeWordIndex } = useLumi();
  if (!spokenSentence || !spokenSentence.words?.length) return null;
  return (
    <div className="lumi-sentence-display" data-testid="lumi-sentence-playback">
      {spokenSentence.words.map((w, i) => (
        <span
          key={`${w}-${i}`}
          className={`lumi-sentence-word${i === activeWordIndex ? " is-active" : ""}`}
          data-testid={`lumi-word-${i}`}
        >
          {w}
        </span>
      ))}
    </div>
  );
};

// ----------------------------------------------------------------------
// Fixed bottom-right sticker — bubble ABOVE per spec
// ----------------------------------------------------------------------
const LumiCharacter = ({ state = "idle", message = "", onTap, inline = false }) => {
  const src = LUMI_SPRITES[state] || LUMI_SPRITES.idle;
  const spriteClass = `lumi-sprite-${state}`;

  return (
    <div
      className={`fixed z-[60] bottom-4 right-3 sm:bottom-6 sm:right-6 ${inline ? "hidden sm:flex" : "flex"} flex-row items-start sm:flex-col sm:items-end gap-2 pointer-events-none`}
      data-testid="lumi-character"
      data-state={state}
    >
      {/* Bubble ABOVE Lumi */}
      {message && (
        <div
          className="lumi-bubble-pop pointer-events-auto relative max-w-[190px] sm:max-w-[280px] bg-white border-2 sm:border-4 border-foreground rounded-2xl shadow-brutal-sm px-3 py-2 sm:px-4 sm:py-3"
          data-testid="lumi-message"
        >
          <p className="font-nunito text-sm text-foreground pr-5 whitespace-pre-wrap">{message}</p>
          <SpeakerHigh size={14} weight="duotone" className="absolute top-2 right-2 text-accent-purple opacity-70" />
          {/* Tail pointing DOWN toward Lumi */}
          <div className="hidden sm:block absolute -bottom-[12px] right-8 w-0 h-0 border-l-[10px] border-r-[10px] border-t-[14px] border-l-transparent border-r-transparent border-t-foreground" />
          <div className="hidden sm:block absolute -bottom-[8px] right-9 w-0 h-0 border-l-[8px] border-r-[8px] border-t-[10px] border-l-transparent border-r-transparent border-t-white" />
        </div>
      )}

      <div className="relative pointer-events-auto cursor-pointer" onClick={onTap}>
        <div className="lumi-halo" />
        {(state === "celebrate" || state === "wave" || state === "speaking") && (
          <>
            <span className="lumi-sparkle lumi-sparkle-1">✦</span>
            <span className="lumi-sparkle lumi-sparkle-2">✧</span>
          </>
        )}
        <img
          src={src}
          alt={message ? "" : (SPRITE_ALT[state] || SPRITE_ALT.idle)}
          draggable={false}
          className={`relative w-24 h-28 sm:w-44 sm:h-56 object-contain object-bottom select-none drop-shadow-[0_8px_12px_rgba(168,85,247,0.30)] ${spriteClass}`}
          data-state={state}
          data-testid="lumi-sprite"
        />
      </div>
    </div>
  );
};

export default LumiCharacter;

// Inline Lumi for small screens inside an activity — sits in the page flow so she never covers controls.
export const LumiInline = ({ className = "" }) => {
  const { state, message, inline } = useLumi();
  if (!inline) return null;
  const src = LUMI_SPRITES[state] || LUMI_SPRITES.idle;
  return (
    <div className={`sm:hidden flex items-center gap-3 ${className}`} data-testid="lumi-inline" data-state={state}>
      <img
        src={src}
        alt={message ? "" : (SPRITE_ALT[state] || SPRITE_ALT.idle)}
        draggable={false}
        className={`w-16 h-20 object-contain object-bottom shrink-0 lumi-sprite-${state}`}
        data-testid="lumi-inline-sprite"
      />
      <p className="font-nunito font-semibold text-sm text-foreground bg-white/80 border-2 border-foreground rounded-2xl px-3 py-2 min-h-[44px] flex items-center flex-1" data-testid="lumi-inline-message">
        {message || "I'm right here with you."}
      </p>
    </div>
  );
};
